import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import Subheading from "../../atoms/typography/Subheading";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import TextField from "../../atoms/inputs/TextField";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import BodySmall from "../../atoms/typography/BodySmall";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import Checkbox from "../../atoms/inputs/Checkbox";
import TableRow from "../../atoms/TableRow";
import Divider from "../../atoms/Divider";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../../atoms/icons/ChevronIcons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import DeleteModal from "../../molecules/modals/DeleteModal";
import type {
  SkynetUserGroup,
  SkynetUserGroupMember,
  SkynetUserGroupPermissions,
} from "../../../models/RolesAndPermissionsModels";
import GroupsPermissionsList from "../../molecules/users/GroupsPermissionsList";
import { useCreateGroup } from "../../../graphql/hooks/rolesAndPermissions/useCreateGroup";
import Alert from "../../molecules/Alert";
import { useSetGroupAdmin } from "../../../graphql/hooks/rolesAndPermissions/useSetGroupAdmin";
import { AddIcon } from "../../atoms/icons/AddIcon";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import TableHeader from "../../atoms/TableHeader";
import TextButton from "../../atoms/buttons/TextButton";
import AddMemberComponent from "./AddMemberComponent";
import { useNavigate } from "react-router-dom";

type CreateGroupInput = {
  name: string;
  description?: string;
  permissions?: SkynetUserGroupPermissions;
  groupAdminSkynetUserIds?: string[];
  skynetUserIds?: string[];
};

interface Props {
  group?: SkynetUserGroup;
  onClose?: () => void;
  onSuccess?: () => void;
  editable?: boolean;
  onDeleteGroup?: (groupId: string) => void;
  groupCopy?: CreateGroupInput;
  groupCopySkynetUsers?: Array<SkynetUserGroupMember>;
}

const emptySets = () => ({
  defaultRead: [],
  defaultWrite: [],
  maxRead: [],
  maxWrite: [],
});

// A brand new group starts with nothing selected, so the admin picks the
// permissions they want rather than unpicking the ones they don't.
const emptyPermissions: SkynetUserGroupPermissions = {
  admin: emptySets(),
  aml: emptySets(),
  client: emptySets(),
  file: emptySets(),
  invoice: emptySets(),
  onboarding: emptySets(),
};

const GreyOutlinedContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 16px;
`;

const ManageGroupModalContent: FC<Props> = ({
  group,
  onClose,
  onSuccess,
  editable = true,
  onDeleteGroup,
  groupCopy,
  groupCopySkynetUsers,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState(
    group?.name || groupCopy?.name || "",
  );
  const [groupDescription, setGroupDescription] = useState(
    group?.description || groupCopy?.description || "",
  );
  const [displayUsers, setDisplayUsers] = useState(false);
  const [permissions, setPermissions] = useState<SkynetUserGroupPermissions>(
    group?.permissions ?? groupCopy?.permissions ?? emptyPermissions,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addMembersOpen, setAddMembersOpen] = useState(false);

  const [newGroupAdminIds, setNewGroupAdminIds] = useState<string[]>(
    groupCopy?.groupAdminSkynetUserIds ?? [],
  );
  const [newGroupMemberIds, setNewGroupMemberIds] = useState<string[]>(
    groupCopy?.skynetUserIds ?? [],
  );

  const onCompletedCreateGroup = (result?: any) => {
    if (result?.rolesAndPermissions?.createGroup?.success) {
      const newGroupId = result?.rolesAndPermissions?.createGroup?.id;
      if (newGroupId) {
        setTimeout(() => {
          navigate(`/groups/${newGroupId}`);
        }, 200);
      }
      setErrorMessage(null);
      onSuccess?.();
      onClose?.();
    } else {
      setErrorMessage("Failed to create group. Please try again.");
    }
  };

  const onErrorCreateGroup = (error?: any) => {
    console.error("Error creating group:", error);
    setErrorMessage(
      error?.message || "An unexpected error occurred. Please try again.",
    );
  };
  const { createGroup, error, loading } = useCreateGroup({
    onCompleted: onCompletedCreateGroup,
    onError: onErrorCreateGroup,
  });

  const isValid = groupName.trim() !== "" && groupDescription.trim() !== "";

  const onClickSave = async () => {
    const { __typename: _pt, ...permissionsWithoutTypename } = (permissions ??
      {}) as typeof permissions & { __typename?: string };
    const strippedPermissions = Object.fromEntries(
      Object.entries(permissionsWithoutTypename).map(([key, val]) => {
        if (!val) return [key, val];
        const { __typename: _vt, ...rest } = val as typeof val & {
          __typename?: string;
        };
        return [key, rest];
      }),
    );

    const createGroupInput: CreateGroupInput = {
      name: groupName,
      description: groupDescription,
      permissions: strippedPermissions as typeof permissions,
      groupAdminSkynetUserIds: newGroupAdminIds,
      skynetUserIds: newGroupMemberIds,
    };

    try {
      await createGroup({
        variables: {
          input: createGroupInput,
        },
      });
    } catch (err) {
      console.error("Error creating group:", err);
    }
  };

  const onCompletedSetGroupAdmin = (result?: any) => {
    if (result?.rolesAndPermissions?.setGroupAdmin?.success) {
      console.log("Group admin set successfully:", result);
    } else {
      console.error("Failed to set group admin. Please try again.");
      setErrorMessage(
        result?.errors?.message ??
          "Failed to update group admin. Please try again.",
      );
    }
  };

  const onErrorSetGroupAdmin = (error?: any) => {
    console.error("Error setting group admin:", error);
    setErrorMessage(
      error?.message ?? "Failed to update group admin. Please try again.",
    );
  };

  const { setGroupAdmin } = useSetGroupAdmin({
    onCompleted: onCompletedSetGroupAdmin,
    onError: onErrorSetGroupAdmin,
  });

  const onClickSetGroupAdmin = async (memberId: string, groupId?: string) => {
    if (!groupId) {
      setNewGroupAdminIds((prev) => [...prev, memberId]);
      return;
    }
    try {
      await setGroupAdmin({
        variables: {
          input: {
            groupId,
            memberId,
          },
        },
      });
    } catch (err) {
      console.error("Error setting group admin:", err);
    }
  };

  return (
    <div>
      <Subheading color={theme.colors.primary}>
        {group ? group?.name : (groupCopy?.name ?? t("users.addGroup"))}
      </Subheading>
      <HorizontalSpacer spacing={2} />
      <Row>
        <Col lg={6}>
          <TextField
            label={t("random.name")}
            fullWidth
            value={groupName}
            onChange={(v) => setGroupName(v)}
            disabled={!editable}
          />
          <HorizontalSpacer spacing={1} />
          <MultilineTextField
            label={t("invoices.description")}
            value={groupDescription}
            onChange={(value) => setGroupDescription(value)}
            disabled={!editable}
            rows={3}
          />
          <HorizontalSpacer spacing={1} />

          {group?.members && (
            <GreyOutlinedContainer>
              <Row
                align="center"
                justify="between"
                nogutter
                onClick={() => setDisplayUsers(!displayUsers)}
                style={{ cursor: "pointer" }}
              >
                <BodySmall fontweight={500}>{t("menu.users")}</BodySmall>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <BodySmall color={theme.colors.textMuted}>
                    {group?.memberCount} {t("users.members")}
                  </BodySmall>
                  {displayUsers ? (
                    <ChevronSmallUpIcon />
                  ) : (
                    <ChevronSmallDownIcon />
                  )}
                </div>
              </Row>
              <SlideOutHeight isVisible={displayUsers}>
                <HorizontalSpacer spacing={1} />
                <TableHeader>
                  <Col lg={9}>
                    <TableHeaderText>Namn</TableHeaderText>
                  </Col>
                  <Col lg={2}>
                    <TableHeaderText>Gruppadmin</TableHeaderText>
                  </Col>
                  <Col lg={1}></Col>
                </TableHeader>
                {group?.members.map((user) => (
                  <TableRow
                    key={user?.skynetUserId}
                    clickable={false}
                    justify="between"
                    align="center"
                  >
                    <Col lg={9}>
                      <BodySmall>
                        {user?.skynetUser?.firstName}{" "}
                        {user?.skynetUser?.lastName}
                      </BodySmall>
                    </Col>
                    <Col lg={2}>
                      <Checkbox
                        checked={user?.isGroupAdmin}
                        onChange={() =>
                          onClickSetGroupAdmin(group?.id, user?.id)
                        }
                      />
                    </Col>
                    <Col
                      lg={1}
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <DeleteModal
                        deleteButtonType="iconButtonMedium"
                        onClickDelete={() => {}}
                      />
                    </Col>
                  </TableRow>
                ))}
                <HorizontalSpacer spacing={0.5} />
              </SlideOutHeight>
            </GreyOutlinedContainer>
          )}

          {group?.members && (
            <div>
              <Row nogutter justify="end">
                <TextButton
                  icon={<AddIcon color={theme.colors.primary} />}
                  title={t("users.addUser")}
                  onClick={() => setAddMembersOpen(true)}
                  color={theme.colors.primary}
                />
              </Row>
              {addMembersOpen && (
                <div>
                  <HorizontalSpacer spacing={2} />
                  <AddMemberComponent
                    groupId={group.id}
                    onSuccess={() => setAddMembersOpen(false)}
                  />
                </div>
              )}
            </div>
          )}

          {groupCopySkynetUsers && groupCopySkynetUsers.length > 0 && (
            <div>
              <TableHeader>
                <Col lg={9}>
                  <TableHeaderText>Namn</TableHeaderText>
                </Col>
                <Col
                  lg={2}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <TableHeaderText>Gruppadmin</TableHeaderText>
                </Col>
                <Col lg={1}></Col>
              </TableHeader>
              {groupCopySkynetUsers
                .filter((user) => newGroupMemberIds.includes(user.skynetUserId))
                .map((user) => (
                  <TableRow
                    key={user.skynetUserId}
                    clickable={false}
                    justify="between"
                    align="center"
                  >
                    <Col lg={9}>
                      <BodySmall>
                        {user.skynetUser?.firstName} {user.skynetUser?.lastName}
                      </BodySmall>
                    </Col>
                    <Col
                      lg={2}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <Checkbox
                        checked={newGroupAdminIds.includes(user.skynetUserId)}
                        onChange={() => {
                          if (newGroupAdminIds.includes(user.skynetUserId)) {
                            setNewGroupAdminIds((prev) =>
                              prev.filter((id) => id !== user.skynetUserId),
                            );
                          } else {
                            setNewGroupAdminIds((prev) => [
                              ...prev,
                              user.skynetUserId,
                            ]);
                          }
                        }}
                      />
                    </Col>
                    <Col
                      lg={1}
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <DeleteModal
                        deleteButtonType="iconButtonMedium"
                        onClickDelete={() => {
                          setNewGroupMemberIds((prev) =>
                            prev.filter((id) => id !== user.skynetUserId),
                          );
                          setNewGroupAdminIds((prev) =>
                            prev.filter((id) => id !== user.skynetUserId),
                          );
                        }}
                      />
                    </Col>
                  </TableRow>
                ))}
            </div>
          )}
        </Col>
        <Col lg={6}>
          <GroupsPermissionsList
            userGroupPermissions={permissions}
            editable={editable}
            onPermissionsChange={editable ? setPermissions : undefined}
            showMaxPermissions={!group}
          />
        </Col>
      </Row>

      <HorizontalSpacer spacing={2} />

      <div>
        <HorizontalSpacer spacing={2} />

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />
        {(error || errorMessage) && (
          <Alert
            type="error"
            title="Gruppen kunde inte skapas"
            message={errorMessage}
          />
        )}
        <Row justify="end" nogutter style={{ gap: 24 }}>
          {onClose && (
            <DefaultButton title={t("common.cancel")} onClick={onClose} />
          )}
          {group?.id && onDeleteGroup && (
            <DeleteModal
              deleteButtonType="outlinedButtonMedium"
              onClickDelete={() => onDeleteGroup?.(group.id)}
              disabled={group?.isSystemGroup}
            />
          )}
          <ActionButton
            title={group ? t("common.save") : t("users.addGroup")}
            onClick={onClickSave}
            color={theme.colors.primary}
            disabled={!editable || !isValid}
            loading={loading}
          />
        </Row>
      </div>
    </div>
  );
};

export default ManageGroupModalContent;
